import { Badge, Card, IconTile, SpaceIcon } from "@design";
import { Lock, Star, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { SpaceSummary } from "@/api";
import { Text } from "@/components/Text";
import { hueOf, spaceIcon } from "@/features/shell/spaceIcons";
import { formatList, formatRelativeTime } from "@/i18n/format";

function SpaceBadge({ space }: Readonly<{ space: SpaceSummary }>) {
  const { t } = useTranslation("spaces");
  if (space.isPersonal) return <Badge icon={<Lock size={11} />}>{t("card.onlyYou")}</Badge>;
  const teams = space.teamNames ?? [];
  if (teams.length === 0) return null;
  return <Badge icon={<Users size={11} />}>{formatList(teams)}</Badge>;
}

/** One space in the grid. The whole card opens the space; the star toggles its sidebar pin. */
export function SpaceCard({
  space,
  starred,
  onToggleStar,
}: Readonly<{ space: SpaceSummary; starred: boolean; onToggleStar: () => void }>) {
  const { t } = useTranslation("spaces");
  return (
    <Card interactive className="group relative flex w-full flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        {space.isPersonal ? (
          <IconTile size="sm" variant="brand">
            <Lock size={14} />
          </IconTile>
        ) : (
          <SpaceIcon icon={spaceIcon(space.icon)} hue={hueOf(space.color)} size="md" />
        )}
        <span className="flex min-w-0 items-center gap-1.5">
          {!space.isPersonal && (
            <button
              type="button"
              aria-label={t(starred ? "card.unstar" : "card.star", { name: space.name })}
              aria-pressed={starred}
              onClick={onToggleStar}
              className={[
                "relative z-10 grid size-6 shrink-0 cursor-pointer place-items-center rounded transition-all hover:text-warning btn-focus-ring",
                starred
                  ? "text-warning"
                  : "text-content-tertiary opacity-100 md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100",
              ].join(" ")}
            >
              <Star size={14} className={starred ? "fill-current" : ""} />
            </button>
          )}
          <SpaceBadge space={space} />
        </span>
      </div>
      <Link
        to={`/spaces/${space.uuid}`}
        className="truncate rounded after:absolute after:inset-0 after:rounded-xl focus-visible:outline-none btn-focus-ring"
      >
        <Text as="span" variant="body-sm" weight="semibold">
          {space.name}
        </Text>
      </Link>
      <Text variant="caption" tone="secondary">
        {t("card.chats", { count: space.chatCount ?? 0 })}
        {" · "}
        {t("card.updated", { when: formatRelativeTime(space.updatedAt) })}
      </Text>
    </Card>
  );
}
